import { useMutation } from '@tanstack/react-query';
import { api } from '@app/utils/api';
import { PANEl_BASE_URL } from '@app/contstants/endpoints';
import { ERR_BAD_REQUEST } from '@app/contstants/http-codes';
import { ServerError } from '@app/error/ServerError';
import {
  AnotherClientApiType,
  IAnotherClientEntry,
} from '@entities/account/IAccount';
import { ISelectOption } from '@widgets/multi-select';
import { AxiosError } from 'axios';

/**
 * Контракт Django `AnotherClientPayloadSerializer` (accounts/serializers.py), без правок бэка:
 *
 * - client_id: IntegerField
 * - type: "NONE" | "MINING" | "REFERRAL" | "AGENT"
 *
 * NONE: только client_id + type (без mining_terms / referrals / referral_account).
 *
 * MINING: + mining_terms — MiningTermsSerializer: cashback, discount (Decimal, 2 знака), оба обязательны.
 *
 * AGENT: + referrals — list[AgentReferralSerializer]: account обязателен; cashback, discount опциональны.
 *   В элементах referrals НЕТ client_id (только у referral_account).
 *
 * REFERRAL: + referral_account — list[ReferralAccountEntrySerializer]: account обязателен;
 *   client_id, cashback, discount опциональны.
 */

/** Элемент referrals для type=AGENT */
type AgentReferralPayload = {
  account: string;
  cashback?: string;
  discount?: string;
};

/** Элемент referral_account для type=REFERRAL */
type ReferralAccountPayload = {
  account: string;
  client_id?: number | null;
  cashback?: string;
  discount?: string;
};

export type AddAnotherClientBody =
  | { client_id: number; type: 'NONE' }
  | {
      client_id: number;
      type: 'MINING';
      mining_terms: { cashback: string; discount: string };
    }
  | { client_id: number; type: 'AGENT'; referrals: AgentReferralPayload[] }
  | {
      client_id: number;
      type: 'REFERRAL';
      referral_account: ReferralAccountPayload[];
    };

function formatDecimal(n: number): string {
  return n.toFixed(2);
}

function parseDecimalInput(s: string): number | undefined {
  const t = s.replace(',', '.').trim();
  if (t === '') return undefined;
  const n = Number(t);
  if (Number.isNaN(n)) return undefined;
  return Math.round(n * 100) / 100;
}

/** IntegerField на бэке — в JSON уходит целое число. */
function normalizeClientId(clientId: number): number {
  const n = Math.trunc(Number(clientId));
  if (!Number.isFinite(n)) {
    throw new Error('Некорректный client_id');
  }
  return n;
}

function mapRowsToAccountEntries(
  accountRows: AnotherClientAccountRowInput[]
): Array<{ account: string; cashback?: number; discount?: number }> {
  return accountRows
    .map(r => ({
      account: String(r.linkedAccount?.value ?? '').trim(),
      cashback: parseDecimalInput(r.cashback ?? ''),
      discount: parseDecimalInput(r.discount ?? ''),
    }))
    .filter(r => r.account.length > 0);
}

function buildAgentReferrals(
  rows: Array<{ account: string; cashback?: number; discount?: number }>
): AgentReferralPayload[] {
  return rows.map(r => {
    const entry: AgentReferralPayload = { account: r.account };
    if (r.cashback !== undefined) entry.cashback = formatDecimal(r.cashback);
    if (r.discount !== undefined) entry.discount = formatDecimal(r.discount);
    return entry;
  });
}

function buildReferralAccounts(
  rows: Array<{ account: string; cashback?: number; discount?: number }>
): ReferralAccountPayload[] {
  return rows.map(r => {
    const entry: ReferralAccountPayload = { account: r.account };
    if (r.cashback !== undefined) entry.cashback = formatDecimal(r.cashback);
    if (r.discount !== undefined) entry.discount = formatDecimal(r.discount);
    return entry;
  });
}

export type AnotherClientAccountRowInput = {
  linkedAccount: ISelectOption | null;
  cashback: string;
  discount: string;
};

export function buildAddAnotherClientBody(input: {
  clientId: number;
  type: AnotherClientApiType;
  miningCashback?: string;
  miningDiscount?: string;
  accountRows: AnotherClientAccountRowInput[];
}): AddAnotherClientBody {
  const {
    clientId,
    type,
    miningCashback = '',
    miningDiscount = '',
    accountRows,
  } = input;

  const client_id = normalizeClientId(clientId);

  switch (type) {
    case 'NONE':
      return { client_id, type: 'NONE' };

    case 'MINING': {
      const c = parseDecimalInput(miningCashback);
      const d = parseDecimalInput(miningDiscount);
      if (c === undefined || d === undefined) {
        throw new Error('Укажите кэшбэк и скидку для типа MINING (mining_terms)');
      }
      return {
        client_id,
        type: 'MINING',
        mining_terms: {
          cashback: formatDecimal(c),
          discount: formatDecimal(d),
        },
      };
    }

    case 'AGENT': {
      const rows = mapRowsToAccountEntries(accountRows);
      if (rows.length === 0) {
        throw new Error(
          'Для агентского типа выберите хотя бы один реферальный ViaBTC-аккаунт (referrals[].account)'
        );
      }
      return {
        client_id,
        type: 'AGENT',
        referrals: buildAgentReferrals(rows),
      };
    }

    case 'REFERRAL': {
      const rows = mapRowsToAccountEntries(accountRows);
      if (rows.length === 0) {
        throw new Error(
          'Для реферального типа выберите агентский ViaBTC-аккаунт привязки (referral_account[].account)'
        );
      }
      return {
        client_id,
        type: 'REFERRAL',
        referral_account: buildReferralAccounts(rows),
      };
    }

    default: {
      const _exhaustive: never = type;
      throw new Error(`Неизвестный тип: ${_exhaustive}`);
    }
  }
}

export async function postAddAnotherClient(
  account: string,
  body: AddAnotherClientBody
): Promise<{ message: string; another_clients: IAnotherClientEntry[] }> {
  try {
    const res = await api.post<{ message: string; another_clients: IAnotherClientEntry[] }>(
      `${PANEl_BASE_URL}/accounts/accounts/${account}/add-another-client/`,
      body
    );
    return res.data;
  } catch (e: unknown) {
    const err = e as AxiosError<Record<string, unknown>>;
    if (err.response?.status === ERR_BAD_REQUEST) {
      const data = err.response.data ?? {};
      let msg = 'Ошибка валидации';
      if (typeof data.detail === 'string') {
        msg = data.detail;
      } else {
        const vals = Object.values(data);
        const first = vals[0];
        if (Array.isArray(first) && typeof first[0] === 'string') {
          msg = first[0];
        } else if (typeof first === 'string') {
          msg = first;
        } else if (vals.length) {
          msg = JSON.stringify(data);
        }
      }
      throw new ServerError(msg);
    }
    throw e;
  }
}

export const useAddAnotherClient = () => {
  const { mutateAsync, isPending, isError, error, isSuccess } = useMutation({
    mutationFn: ({ account, body }: { account: string; body: AddAnotherClientBody }) =>
      postAddAnotherClient(account, body),
  });

  return {
    fetch: mutateAsync,
    isPending,
    isError,
    error,
    isSuccess,
  };
};
